import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

import { DialogRoleEnumResponse, Equipment } from '@libs/entity-lib';
import { DialogService } from '@libs/util-services';
import { addIcons } from 'ionicons';
import {
  basketballOutline,
  chevronDownOutline,
  chevronUpOutline,
  imageOutline,
} from 'ionicons/icons';
import { EquipmentService } from '../../../services/equipment/equipment.service';
import { QuantityStepperComponent } from '../quantity-stepper/quantity-stepper.component';

/**
 * Seleção de equipamentos disponíveis para um slot+esporte.
 *
 * Mobile-first: card por equipamento empilha em coluna única < 600px;
 * em telas maiores (>= 600px) o `equipments-grid` vira 2 colunas via
 * `auto-fill` + `minmax(280px, 1fr)` — adapta de 1 até N colunas conforme
 * o espaço sem media query manual.
 *
 * Emite `selectionChange` sempre que a seleção muda — o pai mantém o
 * payload final pronto para o submit, sem chamadas extras.
 */
import { IonIcon, IonSpinner } from '@ionic/angular/standalone';

@Component({
  selector: 'lessor-equipment-picker',
  standalone: true,
  imports: [
    IonIcon,
    IonSpinner,
    CommonModule,
    QuantityStepperComponent
  ],
  templateUrl: './equipment-picker.component.html',
  styleUrls: ['./equipment-picker.component.scss'],
  providers: [DialogService],
})
export class EquipmentPickerComponent {
  private readonly equipmentService = inject(EquipmentService);
  private readonly dialogService = inject(DialogService);

  readonly date = input<string | null>(null);
  readonly provideCourtId = input<number | null>(null);
  readonly sportId = input<number | null>(null);
  /** Limite por unidade (1 por vaga, default). Quando 0, esconde o stepper. */
  readonly maxPerEquipment = input<number>(1);

  readonly selectionChange = output<{ id: number; amountEquipment: number }[]>();
  readonly totalChange = output<number>();

  readonly equipments = signal<Equipment[]>([]);
  readonly loading = signal<boolean>(false);
  readonly errorMsg = signal<string | null>(null);
  readonly isOpen = signal<boolean>(false);
  /** Map id → quantidade selecionada. */
  readonly quantities = signal<Record<number, number>>({});

  readonly hasEquipments = computed(() => this.equipments().length > 0);
  readonly selectedCount = computed(() =>
    Object.values(this.quantities()).reduce((s, q) => s + q, 0),
  );
  readonly totalValue = computed(() => {
    const q = this.quantities();
    return this.equipments().reduce((acc, eq) => acc + (q[eq.id] ?? 0) * eq.value, 0);
  });

  constructor() {
    addIcons({
      basketballOutline,
      chevronDownOutline,
      chevronUpOutline,
      imageOutline,
    });

    // Fetch dispara quando o trio (date, provideCourtId, sportId) muda E a seção está aberta.
    // Antes de o usuário abrir, não consome rede — busca ociosa só na 1ª expansão.
    // O sync de quantidades válidas (descartar ids que sumiram) acontece dentro
    // do callback do fetch, fora de qualquer effect — evita NG0100 que ocorre
    // quando um effect escreve em signal durante o change detection do pai.
    effect(() => {
      const date = this.date();
      const courtId = this.provideCourtId();
      const sportId = this.sportId();
      if (!this.isOpen() || !date || !courtId || !sportId) return;
      this.loadEquipments(date, courtId, sportId);
    });
  }

  toggleOpen(): void {
    this.isOpen.update((v) => !v);
  }

  getQty(id: number): number {
    return this.quantities()[id] ?? 0;
  }

  async onQtyChange(eq: Equipment, next: number): Promise<void> {
    const cur = this.getQty(eq.id);
    // Saindo de 1 → 0: pergunta confirmação. Outras transições aplicam direto.
    if (cur === 1 && next === 0) {
      const result = await this.dialogService.confirmationPopup(
        `Remover ${eq.name} da reserva?`,
      );
      if (result.role !== DialogRoleEnumResponse.YES) return;
      this.quantities.update((q) => {
        const copy = { ...q };
        delete copy[eq.id];
        return copy;
      });
      this.emitSelection();
      return;
    }
    if (next <= 0) return;
    this.quantities.update((q) => ({ ...q, [eq.id]: next }));
    this.emitSelection();
  }

  private emitSelection(): void {
    const q = this.quantities();
    const selection = Object.entries(q)
      .filter(([, amount]) => amount > 0)
      .map(([id, amount]) => ({ id: Number(id), amountEquipment: amount }));
    this.selectionChange.emit(selection);
    this.totalChange.emit(this.totalValue());
  }

  private loadEquipments(date: string, courtId: number, sportId: number): void {
    this.loading.set(true);
    this.errorMsg.set(null);
    this.equipmentService.fetchByRent(date, courtId, sportId).subscribe({
      next: (list) => {
        const next = list ?? [];
        this.equipments.set(next);
        // Limpa quantidades de equipamentos que sumiram (sport mudou, etc).
        // Feito fora de effect — sync direto no callback, sem race com CD do pai.
        this.pruneQuantities(next.map((e) => e.id));
        this.loading.set(false);
      },
      error: () => {
        this.errorMsg.set('Não foi possível carregar os equipamentos.');
        this.loading.set(false);
      },
    });
  }

  private pruneQuantities(validIds: number[]): void {
    const valid = new Set(validIds);
    const current = this.quantities();
    const trimmed: Record<number, number> = {};
    for (const id of Object.keys(current)) {
      const idNum = Number(id);
      if (valid.has(idNum)) trimmed[idNum] = current[idNum];
    }
    if (Object.keys(trimmed).length !== Object.keys(current).length) {
      this.quantities.set(trimmed);
      this.emitSelection();
    }
  }
}
